import FocusTrap from "focus-trap-react";

import { useTheme } from "../../../../../context/theme";
import { IconButton } from "../../../../controls";
import { Box } from "../../../../layout";
import StandardContentLayout from "./components/StandardContentLayout/StandardContentLayout";
import { MAX_WIDTH_MAP } from "./consts";
import { isStandardContent } from "./utils";

import type { ResponsiveDimension, Dimension } from "../../../../../types";

import type { ModalProps } from "../../Modal";
import { useBreakpoint } from "../../../../../hooks";

type Props = Required<
  Pick<
    ModalProps,
    | "children"
    | "inContainer"
    | "padding"
    | "maxWidth"
    | "fullscreenOnMobile"
    | "overflowY"
    | "noTopImageContainer"
  >
> &
  Pick<ModalProps, "onCloseClick" | "allowOutsideClick">;

export default function ContentBox({
  children,
  inContainer,
  onCloseClick,
  padding,
  maxWidth,
  fullscreenOnMobile,
  overflowY,
  noTopImageContainer,
  allowOutsideClick
}: Props) {
  const theme = useTheme();

  const contentIsStandard = isStandardContent(children);

  const modalStyles =
    fullscreenOnMobile && !inContainer
      ? {
          height: ["100%", "unset"] as [string, string],
          borderRadius: ["none", "12"] as ResponsiveDimension
        }
      : { height: "unset", borderRadius: "12" as Dimension };

  const { isMobileOnly } = useBreakpoint();

  const isFocusTrapActive = !inContainer && !isMobileOnly;

  return (
    <FocusTrap
      active={isFocusTrapActive}
      focusTrapOptions={{
        allowOutsideClick
      }}
    >
      <Box
        position={inContainer ? "absolute" : "fixed"}
        left="50%"
        top="50%"
        transform="translate(-50%, -50%)"
        margin="auto"
        paddingY={contentIsStandard ? "xxlarge" : padding}
        paddingX={
          contentIsStandard ? "xxlarge" : ["xlarge", padding as Dimension]
        }
        maxWidth={contentIsStandard ? "560px" : MAX_WIDTH_MAP[maxWidth]}
        width="100%"
        maxHeight={["100%", "90%"]}
        overflowY={overflowY}
        backgroundColor={theme.foreground}
        {...modalStyles}
        dataTest="Modal"
        UNSAFE_role="dialog"
        aria-modal={!inContainer}
      >
        {onCloseClick && (
          <Box position="absolute" top="12px" right="12px">
            <IconButton
              iconName="cross"
              accessibilityLabel="Close modal"
              onClick={onCloseClick}
              size="small"
              tone="neutral"
            />
          </Box>
        )}
        {contentIsStandard ? (
          <StandardContentLayout noTopImageContainer={noTopImageContainer}>
            {children}
          </StandardContentLayout>
        ) : (
          children
        )}
      </Box>
    </FocusTrap>
  );
}
